// ── POS 엑셀 가져오기 매퍼 ─────────────────────────────────────
// 한국 POS 시스템(키오스크·테이블오더 등)이 내보내는 매출 엑셀을
// 셈핏의 transactions + lineItems 구조로 변환.
//
// 지원 시트 자동 감지:
//   - "결제 합계"           — 일별 매출 + 결제수단별
//   - "상품 주문 합계"       — 일별 메뉴별 매출 (lineItems 소스)
//   - "결제 상세내역" / "상품 주문 상세내역" — 미사용 (참고용)
//
// 결과:
//   - 일자당 결제수단별 transaction (cash·card·qr·transfer) 1~4건
//   - 첫 transaction에 lineItems[] 첨부 (메뉴 판매 수량·금액·VAT)
//   - 중복 방지: source='pos-import' + dateKey로 식별

/* global XLSX */

// 파일 → ArrayBuffer
async function readFileBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// 시트 → row 배열
function sheetToRows(wb, sheetName) {
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
}

// 날짜 정규화 — '2025-08-30' / '2025-08-30 00:00:00' / Excel serial → YYYY-MM-DD
function normalizeDate(v) {
  if (!v) return '';
  if (typeof v === 'number') {
    // Excel serial date
    const ms = (v - 25569) * 86400 * 1000;
    return new Date(ms).toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  const m = s.match(/(\d{4})[-./]\s*(\d{1,2})[-./]\s*(\d{1,2})/);
  if (m) {
    return `${m[1]}-${String(m[2]).padStart(2, '0')}-${String(m[3]).padStart(2, '0')}`;
  }
  return s.slice(0, 10);
}

const toNum = (v) => {
  if (typeof v === 'number') return v;
  if (!v) return 0;
  const n = parseFloat(String(v).replace(/[,\s원]/g, ''));
  return isFinite(n) ? n : 0;
};

// ─────────────────────── 결제 합계 시트 파싱 ───────────────────────
// 헤더 패턴:
//   row 0: 기간 | 결제금액 | 부가세 | 결제건수 | 결제수단별 | ... |
//   row 1: ... | 현금 | 카드 | QR결제 | 계좌이체 | 선불지급수단
//   row 2+: 데이터
function parsePaymentSummary(rows) {
  if (!rows || rows.length < 3) return [];

  // 헤더 row 0/1 통합해서 컬럼 매핑
  const h0 = rows[0] || [];
  const h1 = rows[1] || [];
  const colMap = {};
  for (let i = 0; i < Math.max(h0.length, h1.length); i++) {
    const a = String(h0[i] || '').trim();
    const b = String(h1[i] || '').trim();
    const label = (a || b).replace(/\s+/g, '');
    if (label === '기간' || label === '날짜') colMap.date = i;
    else if (label === '결제금액' || label === '매출액') colMap.total = i;
    else if (label === '부가세') colMap.vat = i;
    else if (label === '결제건수' || label === '건수') colMap.count = i;
    else if (b === '현금') colMap.cash = i;
    else if (b === '카드') colMap.card = i;
    else if (b === 'QR결제' || b === 'QR') colMap.qr = i;
    else if (b === '계좌이체' || b === '이체') colMap.transfer = i;
    else if (b === '선불지급수단' || b === '선불') colMap.prepaid = i;
  }

  const out = [];
  for (let r = 2; r < rows.length; r++) {
    const row = rows[r];
    const date = normalizeDate(row[colMap.date]);
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const total = toNum(row[colMap.total]);
    if (total <= 0) continue;
    out.push({
      date,
      total,
      vat: toNum(row[colMap.vat]),
      count: toNum(row[colMap.count]),
      cash: toNum(row[colMap.cash]),
      card: toNum(row[colMap.card]),
      qr: toNum(row[colMap.qr]),
      transfer: toNum(row[colMap.transfer]),
      prepaid: toNum(row[colMap.prepaid]),
    });
  }
  return out;
}

// ─────────────────────── 상품 주문 합계 시트 파싱 ───────────────────────
// 헤더 row 0: 기간 | 상품명 | 상품코드 | 카테고리 | 판매건수 | 상품가격 | 옵션가격 | 할인 | 실 판매 금액 | 부가세액
function parseMenuSummary(rows) {
  if (!rows || rows.length < 2) return [];
  const h = rows[0] || [];
  const colMap = {};
  for (let i = 0; i < h.length; i++) {
    const label = String(h[i] || '').trim().replace(/\s+/g, '');
    if (label === '기간' || label === '날짜') colMap.date = i;
    else if (label === '상품명' || label === '메뉴' || label === '메뉴명') colMap.name = i;
    else if (label === '상품코드' || label === '메뉴코드') colMap.code = i;
    else if (label === '카테고리') colMap.category = i;
    else if (label === '판매건수' || label === '수량') colMap.qty = i;
    else if (label === '상품가격' || label === '판매가') colMap.price = i;
    else if (label === '옵션가격' || label === '옵션') colMap.option = i;
    else if (label === '할인') colMap.discount = i;
    else if (label.includes('실판매')) colMap.amount = i;
    else if (label === '부가세액' || label === '부가세') colMap.vat = i;
  }

  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const date = normalizeDate(row[colMap.date]);
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const name = String(row[colMap.name] || '').trim();
    if (!name) continue;
    const qty = toNum(row[colMap.qty]);
    const amount = toNum(row[colMap.amount]) || toNum(row[colMap.price]);
    if (qty <= 0 && amount <= 0) continue;
    out.push({
      date,
      name,
      category: String(row[colMap.category] || '').trim(),
      quantity: qty,
      unitPrice: qty > 0 ? Math.round(amount / qty) : amount,
      amount,
      vat: toNum(row[colMap.vat]),
      discount: toNum(row[colMap.discount]),
    });
  }
  return out;
}

// ─────────────────────── 메인 파싱 ───────────────────────
export async function parsePosExcel(file) {
  let wb;
  try {
    const buf = await readFileBuffer(file);
    wb = XLSX.read(buf, { type: 'array' });
  } catch (e) {
    if (String(e.message || '').includes('Password') || String(e.message || '').includes('encrypted')) {
      throw new Error('비밀번호 보호된 파일입니다. 엑셀에서 비밀번호 해제 후 저장하고 다시 업로드해주세요.');
    }
    throw new Error(`엑셀 읽기 실패: ${e.message}`);
  }

  // 시트 자동 매칭 (정확한 이름 또는 부분 매칭)
  const sheetNames = wb.SheetNames || [];
  const findSheet = (keyword) => sheetNames.find(s => s.replace(/\s+/g, '').includes(keyword.replace(/\s+/g, '')));

  const paySheet = findSheet('결제합계') || findSheet('일별매출') || findSheet('매출합계');
  const menuSheet = findSheet('상품주문합계') || findSheet('메뉴별') || findSheet('상품합계');

  if (!paySheet && !menuSheet) {
    throw new Error('인식 가능한 시트가 없습니다. POS 매출 리포트 형식(결제 합계·상품 주문 합계 시트 포함)을 확인하세요.');
  }

  const payments = paySheet ? parsePaymentSummary(sheetToRows(wb, paySheet)) : [];
  const menus = menuSheet ? parseMenuSummary(sheetToRows(wb, menuSheet)) : [];

  // 일자별 묶기
  const byDate = {};
  for (const p of payments) {
    byDate[p.date] = { ...byDate[p.date], date: p.date, payment: p };
  }
  for (const m of menus) {
    if (!byDate[m.date]) byDate[m.date] = { date: m.date, payment: null };
    if (!byDate[m.date].lineItems) byDate[m.date].lineItems = [];
    byDate[m.date].lineItems.push(m);
  }

  const dayList = Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));

  return {
    fileName: file.name,
    sheets: sheetNames,
    paySheet,
    menuSheet,
    dayList,
    summary: {
      totalDays: dayList.length,
      totalRevenue: dayList.reduce((s, d) => s + (d.payment?.total || 0), 0),
      totalMenus: menus.length,
      dateRange: dayList.length > 0
        ? { start: dayList[dayList.length - 1].date, end: dayList[0].date }
        : null,
    },
  };
}

// ─────────────────────── transactions 변환 ───────────────────────
// 일자별 결제수단 분리 → transaction N개 생성. 첫 tx에 lineItems 첨부.
const REVENUE_CATS = {
  cash: 'sales_cash',
  card: 'sales_card',
  qr: 'sales_card',           // QR은 카드 매출로 (홈택스 분류상)
  transfer: 'sales_card',     // 계좌이체도 카드 매출 카테고리
  prepaid: 'sales_card',
};

const EVIDENCE_TYPE = {
  cash: 'cash',
  card: 'card',
  qr: 'card',
  transfer: 'transfer',
  prepaid: 'card',
};

export function buildTransactionsFromPos(parsed, options = {}) {
  const { source = 'pos-import' } = options;
  const out = [];
  let txIndex = 0;

  for (const day of parsed.dayList) {
    const { date, payment, lineItems = [] } = day;
    if (!payment || payment.total <= 0) continue;

    // 결제수단별 분할
    const splits = [
      { key: 'card',     amt: payment.card,     cat: REVENUE_CATS.card,     evid: EVIDENCE_TYPE.card },
      { key: 'cash',     amt: payment.cash,     cat: REVENUE_CATS.cash,     evid: EVIDENCE_TYPE.cash },
      { key: 'qr',       amt: payment.qr,       cat: REVENUE_CATS.qr,       evid: EVIDENCE_TYPE.qr },
      { key: 'transfer', amt: payment.transfer, cat: REVENUE_CATS.transfer, evid: EVIDENCE_TYPE.transfer },
      { key: 'prepaid',  amt: payment.prepaid,  cat: REVENUE_CATS.prepaid,  evid: EVIDENCE_TYPE.prepaid },
    ].filter(s => s.amt > 0);

    if (splits.length === 0) continue;

    // 합계 검증 — splits 합이 total과 다르면 비례 조정
    const splitSum = splits.reduce((s, x) => s + x.amt, 0);
    const ratio = payment.total / (splitSum || 1);

    // lineItems 정규화 (셈핏 lineItem 스키마)
    const normalizedLineItems = lineItems.map((m, idx) => ({
      id: `pos_${date}_${idx}`,
      name: m.name,
      kind: 'purchase',          // 매출 lineItem (kind는 매입과 호환되지만 매출에선 메뉴 의미)
      quantity: m.quantity,
      unitPrice: m.unitPrice,
      amount: Math.round(m.amount),
      vat: Math.round(m.vat),
      supply: Math.round(m.amount - m.vat),
      isTaxFree: false,          // 한식은 과세
      category: m.category || '',
      memo: m.discount > 0 ? `할인 ${m.discount}원 적용` : '',
    }));

    splits.forEach((split, idx) => {
      const adjusted = Math.round(split.amt * ratio);
      const supply = Math.round(adjusted / 1.1);
      const vat = adjusted - supply;
      const tx = {
        type: 'income',
        date,
        supply, vat, total: adjusted,
        partnerId: null,
        partnerName: 'POS 매출',
        evidence: {
          type: split.evid,
          isQualified: true,
          sourceUrl: null,
          externalId: null,
        },
        category: { major: null, sub: null },
        revenueCategory: split.cat,
        itemName: '',
        memo: `${split.key.toUpperCase()} 매출 (POS 자동 입력)`,
        vatTreatment: { deductible: false, presumed: false, presumedRate: null },
        source,                  // 중복 방지용 식별자
        sourcePaymentMethod: split.key,
        sourceDate: date,
      };
      // 첫 split에만 lineItems 첨부 (중복 방지)
      if (idx === 0 && normalizedLineItems.length > 0) {
        tx.lineItems = normalizedLineItems;
      }
      out.push(tx);
      txIndex++;
    });
  }

  return out;
}

// ─────────────────────── 중복 체크 ───────────────────────
// 기존 거래장부에서 source='pos-import'인 date set
export function findExistingPosDates(transactions, source = 'pos-import') {
  const set = new Set();
  for (const t of transactions || []) {
    if (t.source === source && t.date) set.add(t.date);
  }
  return set;
}

// 합계 포맷
export function formatKRW(n) {
  return (Math.round(Number(n) || 0)).toLocaleString('ko-KR');
}
